import * as d3 from "d3";
import { MemoryModel, MediaModel, Contact, ContactModel, GroupModel } from "common247";
import {
  IFilteredEntityResultSet,
  calculateMemoryDurationDays,
  calculateMemoryTimeDifferenceDays,
  parseDateToEpoch,
} from "./memory-filter-service";

export type VisualiserNodeType = "memory" | "media" | "contact" | "group";

export interface IVisualiserNode extends d3.SimulationNodeDatum {
  id: string;
  type: VisualiserNodeType;
  label: string;
  sublabel?: string;
  size: number; // Block width/height for memory, radius for dots
  durationDays?: number;
  rating?: number;
  color?: string;
  data: MemoryModel | MediaModel | Contact | ContactModel | GroupModel;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
}

export type VisualiserLinkType =
  | "memory-sequence"
  | "memory-media"
  | "memory-contact"
  | "memory-group";

export interface IVisualiserLink extends d3.SimulationLinkDatum<IVisualiserNode> {
  id: string;
  source: string | IVisualiserNode;
  target: string | IVisualiserNode;
  type: VisualiserLinkType;
  distance: number;
  strength?: number;
}

export interface IVisualiserGraph {
  nodes: IVisualiserNode[];
  links: IVisualiserLink[];
}

export interface ISimulationOptions {
  width?: number;
  height?: number;
  ticks?: number;
  alphaMin?: number;
}

/**
 * Calculates the dimensions (width & height in px) for a Memory block
 * proportional to its duration in days.
 *
 * @param {number} durationDays - Duration of the memory in days.
 * @returns {number} Block size dimension (48px to 120px).
 */
export function calculateMemoryBlockSize(durationDays: number): number {
  if (!durationDays || durationDays <= 1) return 48;
  const scaled = 48 + Math.log2(durationDays) * 14;
  return Math.min(120, Math.max(48, Math.round(scaled)));
}

/**
 * Calculates the radius for a Media dot based on its FavouriteRating (1 to 5).
 * 1 is smallest, 2 is small, 3 is medium, 4 is large, 5 is biggest.
 *
 * @param {number} [rating] - Favourite rating (1-5).
 * @returns {number} Dot radius in pixels (6px to 24px).
 */
export function calculateMediaDotRadius(rating?: number): number {
  switch (rating) {
    case 1:
      return 6;
    case 2:
      return 10;
    case 3:
      return 14;
    case 4:
      return 18;
    case 5:
      return 24;
    default:
      return 8;
  }
}

/**
 * Calculates the distance between two chronological memories based on time difference in days.
 *
 * @param {number} days - Time delta between memories in days.
 * @returns {number} Link distance in pixels (80px to 450px).
 */
export function calculateMemoryLinkDistance(days: number): number {
  if (days <= 0) return 80;
  return Math.min(450, Math.max(80, Math.round(days * 2.5)));
}

/**
 * Builds the nodes and links graph model for D3 force simulation from filtered entities.
 *
 * @param {IFilteredEntityResultSet} filteredData - Filtered entity result set.
 * @param {number} [canvasWidth=900] - Canvas width.
 * @param {number} [canvasHeight=650] - Canvas height.
 * @returns {IVisualiserGraph} Visualiser nodes and links.
 */
export function buildVisualiserGraph(
  filteredData: IFilteredEntityResultSet,
  canvasWidth = 900,
  canvasHeight = 650
): IVisualiserGraph {
  const nodes: IVisualiserNode[] = [];
  const links: IVisualiserLink[] = [];
  const nodeMap = new Map<string, IVisualiserNode>();

  const memories = filteredData.memories || [];
  const media = filteredData.media || [];
  const contacts = filteredData.contacts || [];
  const groups = filteredData.groups || [];

  // Sort memories chronologically
  const sortedMemories = [...memories].sort((a, b) => {
    const epochA = parseDateToEpoch(a.date) || a.createdAt || 0;
    const epochB = parseDateToEpoch(b.date) || b.createdAt || 0;
    return epochA - epochB;
  });

  // 1. Build Memory Nodes (Blocks)
  sortedMemories.forEach((mem, idx) => {
    const durationDays = calculateMemoryDurationDays(mem);
    const blockSize = calculateMemoryBlockSize(durationDays);
    const node: IVisualiserNode = {
      id: `memory_${mem.id}`,
      type: "memory",
      label: mem.title || "Untitled Memory",
      sublabel: mem.date || "Undated",
      size: blockSize,
      durationDays,
      color: "#F5EBE1", // Heirloom Butter Bean
      data: mem,
      // Spread initially along chronological spiral/circle
      x: canvasWidth / 2 + Math.cos((idx / Math.max(1, sortedMemories.length)) * 2 * Math.PI) * 180,
      y: canvasHeight / 2 + Math.sin((idx / Math.max(1, sortedMemories.length)) * 2 * Math.PI) * 180,
    };
    nodes.push(node);
    nodeMap.set(node.id, node);
  });

  // Connect chronological memory pairs with distance proportional to days delta
  for (let i = 0; i < sortedMemories.length - 1; i++) {
    const memA = sortedMemories[i];
    const memB = sortedMemories[i + 1];
    const daysDelta = calculateMemoryTimeDifferenceDays(memA, memB);
    const linkDistance = calculateMemoryLinkDistance(daysDelta);

    links.push({
      id: `link_mem_${memA.id}_${memB.id}`,
      source: `memory_${memA.id}`,
      target: `memory_${memB.id}`,
      type: "memory-sequence",
      distance: linkDistance,
      strength: 0.7,
    });
  }

  // 2. Build Media Nodes (Dots scaled by favourite rating)
  media.forEach((item) => {
    const rating = (item as any).favouriteRating || (item as any).rating || 1;
    const dotRadius = calculateMediaDotRadius(rating);
    const node: IVisualiserNode = {
      id: `media_${item.id}`,
      type: "media",
      label: (item as any).title || (item as any).filename || "Media Item",
      sublabel: `Rating: ${rating}★`,
      size: dotRadius,
      rating,
      color: "#2563EB", // Archival Blue Linen accent
      data: item,
    };
    nodes.push(node);
    nodeMap.set(node.id, node);

    // Link to associated memory if present
    const memoryId = (item as any).memoryId || (item.metadata as any)?.memory_id;
    if (memoryId && nodeMap.has(`memory_${memoryId}`)) {
      links.push({
        id: `link_med_${item.id}_${memoryId}`,
        source: `memory_${memoryId}`,
        target: node.id,
        type: "memory-media",
        distance: 55,
        strength: 0.9,
      });
    } else {
      // Connect to first matching memory that references this media in its array
      const parentMem = sortedMemories.find((m) =>
        (m.media || []).some((med) => med && med.id === item.id)
      );
      if (parentMem) {
        links.push({
          id: `link_med_${item.id}_${parentMem.id}`,
          source: `memory_${parentMem.id}`,
          target: node.id,
          type: "memory-media",
          distance: 55,
          strength: 0.9,
        });
      }
    }
  });

  // 3. Build Contact Nodes (Dots)
  contacts.forEach((contact) => {
    const cId = contact.id || (contact as any).uid;
    const name = (contact as any).displayName || (contact as any).name || "Contact";
    const node: IVisualiserNode = {
      id: `contact_${cId}`,
      type: "contact",
      label: name,
      sublabel: (contact as any).relationship || "Associate",
      size: 11, // Dot radius
      color: "#D97706", // Heirloom Amber
      data: contact,
    };
    nodes.push(node);
    nodeMap.set(node.id, node);

    // Link contact to memories they participate in
    sortedMemories.forEach((mem) => {
      const memContactIds = new Set(
        ((mem as any).contactIds || []).concat((mem as any).sharedContactIds || [])
      );
      if (memContactIds.has(cId)) {
        links.push({
          id: `link_cnt_${cId}_${mem.id}`,
          source: `memory_${mem.id}`,
          target: node.id,
          type: "memory-contact",
          distance: 65,
          strength: 0.8,
        });
      }
    });
  });

  // 4. Build Group Nodes (Dots)
  groups.forEach((group) => {
    const gId = group.id;
    const name = (group as any).name || (group as any).title || "Group";
    const node: IVisualiserNode = {
      id: `group_${gId}`,
      type: "group",
      label: name,
      sublabel: `${(group as any).members?.length || 0} members`,
      size: 13, // Dot radius
      color: "#059669", // Heirloom Emerald
      data: group,
    };
    nodes.push(node);
    nodeMap.set(node.id, node);

    // Link group to memories they belong to
    sortedMemories.forEach((mem) => {
      const memGroupIds = new Set(
        ((mem as any).groupIds || []).concat((mem as any).sharedGroupIds || [])
      );
      if (memGroupIds.has(gId)) {
        links.push({
          id: `link_grp_${gId}_${mem.id}`,
          source: `memory_${mem.id}`,
          target: node.id,
          type: "memory-group",
          distance: 75,
          strength: 0.8,
        });
      }
    });
  });

  return { nodes, links };
}

/**
 * Runs D3 force simulation to calculate optimal coordinates for all nodes and links.
 * Runs synchronously for the specified number of ticks to ensure deterministic layout.
 *
 * @param {IVisualiserNode[]} rawNodes - Graph nodes.
 * @param {IVisualiserLink[]} rawLinks - Graph links.
 * @param {ISimulationOptions} [options] - Simulation configuration.
 * @returns {IVisualiserGraph} Layout with computed x, y coordinates.
 */
export function runVisualiserSimulation(
  rawNodes: IVisualiserNode[],
  rawLinks: IVisualiserLink[],
  options: ISimulationOptions = {}
): IVisualiserGraph {
  const width = options.width || 900;
  const height = options.height || 650;
  const ticks = options.ticks || 120;

  // Deep clone to prevent mutating callers' references
  const nodes: IVisualiserNode[] = rawNodes.map((n) => ({ ...n }));
  const links: IVisualiserLink[] = rawLinks.map((l) => ({ ...l }));

  if (nodes.length === 0) {
    return { nodes: [], links: [] };
  }

  const simulation = d3
    .forceSimulation<IVisualiserNode>(nodes)
    .force(
      "link",
      d3
        .forceLink<IVisualiserNode, IVisualiserLink>(links)
        .id((d) => d.id)
        .distance((d) => d.distance || 80)
        .strength((d) => d.strength || 0.6)
    )
    .force("charge", d3.forceManyBody().strength(-200))
    .force("center", d3.forceCenter(width / 2, height / 2))
    .force(
      "collision",
      d3.forceCollide<IVisualiserNode>().radius((d) => {
        return d.type === "memory" ? d.size * 0.75 : d.size + 14;
      })
    )
    .stop();

  // Execute simulation ticks
  for (let i = 0; i < ticks; ++i) {
    simulation.tick();
  }

  return { nodes, links };
}

/**
 * Generates an SVG XML string representing the D3 graph layout for archival PDF and image export.
 *
 * @param {IVisualiserNode[]} nodes - Placed graph nodes.
 * @param {IVisualiserLink[]} links - Placed graph links.
 * @param {number} [width=900] - SVG width.
 * @param {number} [height=650] - SVG height.
 * @returns {string} Standalone SVG markup string.
 */
export function generateVisualiserSvgString(
  nodes: IVisualiserNode[],
  links: IVisualiserLink[],
  width = 900,
  height = 650
): string {
  const linksSvg = links
    .map((link) => {
      const src = typeof link.source === "object" ? link.source : nodes.find((n) => n.id === link.source);
      const tgt = typeof link.target === "object" ? link.target : nodes.find((n) => n.id === link.target);
      if (!src || !tgt || src.x === undefined || tgt.x === undefined) return "";

      const strokeColor = link.type === "memory-sequence" ? "#94A3B8" : "#CBD5E1";
      const strokeWidth = link.type === "memory-sequence" ? 2.5 : 1.5;
      const strokeDash = link.type === "memory-sequence" ? "none" : "3,3";

      return `<line x1="${src.x}" y1="${src.y}" x2="${tgt.x}" y2="${tgt.y}" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-dasharray="${strokeDash}" />`;
    })
    .join("\n");

  const nodesSvg = nodes
    .map((node) => {
      const x = node.x ?? 0;
      const y = node.y ?? 0;

      if (node.type === "memory") {
        const half = node.size / 2;
        return `
          <g transform="translate(${x - half}, ${y - half})">
            <rect width="${node.size}" height="${node.size}" fill="#F5EBE1" stroke="#2563EB" stroke-width="2" />
            <text x="${node.size / 2}" y="${node.size / 2 - 2}" text-anchor="middle" font-size="11" font-weight="bold" fill="#1E293B">
              ${escapeXml(node.label.slice(0, 14))}
            </text>
            <text x="${node.size / 2}" y="${node.size / 2 + 12}" text-anchor="middle" font-size="9" fill="#64748B">
              ${node.durationDays || 1}d
            </text>
          </g>
        `;
      }

      // Dots for media, contact, group
      const fill = node.color || "#2563EB";
      return `
        <g transform="translate(${x}, ${y})">
          <circle r="${node.size}" fill="${fill}" stroke="#FFFFFF" stroke-width="1.5" />
          <text x="0" y="${node.size + 11}" text-anchor="middle" font-size="9" fill="#334155">
            ${escapeXml(node.label.slice(0, 10))}
          </text>
        </g>
      `;
    })
    .join("\n");

  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" style="background-color: #FAFAF9; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E2E8F0" stroke-width="0.5"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
      <g id="links-layer">${linksSvg}</g>
      <g id="nodes-layer">${nodesSvg}</g>
    </svg>
  `;
}

function escapeXml(unsafe: string): string {
  return unsafe
    .replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case "&":
          return "&amp;";
        case "'":
          return "&apos;";
        case '"':
          return "&quot;";
        default:
          return c;
      }
    });
}
